// ضغط وتحويل الصورة داخل المتصفح قبل رفعها.
//
// سببان:
// 1) صور الجوال تصل إلى 5-12 ميغابايت والخادم يصغّرها إلى 1400px على أي حال،
//    فرفع نسخة مصغّرة يقلّل زمن الرفع بشكل كبير خاصة على شبكات الجوال.
// 2) الأهم: كاميرا iPhone تنتج ملفات HEIC بضاغط HEVC، ومكتبة sharp على
//    الخادم لا تستطيع فك ترميزها (البناء الجاهز بلا دعم HEVC) فترفض الصورة
//    ولا تُحفظ. متصفح Safari يفك ترميز HEIC أصلًا، لذلك نحوّلها هنا إلى JPEG.
//    (اختيار الصورة من المعرض كان يعمل لأن Safari يحوّلها تلقائيًا.)

const MAX_DIMENSION = 1800;
const JPEG_QUALITY = 0.82;
// أقل من هذا الحجم لا يستحق عناء الضغط — ما لم يكن التحويل إلزاميًا.
const SKIP_BELOW_BYTES = 500 * 1024;
// حارس ضد تعليق toBlob في Safari تحت ضغط الذاكرة: لا ننتظر للأبد.
const CONVERT_TIMEOUT_MS = 20000;

const COMPRESSIBLE_TYPES = new Set(['image/jpeg', 'image/jpg', 'image/png', 'image/webp']);
const HEIC_TYPES = new Set(['image/heic', 'image/heif', 'image/heic-sequence', 'image/heif-sequence']);

function lower(value) {
  return String(value || '').toLowerCase();
}

// iOS أحيانًا يرسل الملف بنوع MIME فارغ، فنعتمد على الامتداد أيضًا.
function isHeic(file) {
  return HEIC_TYPES.has(lower(file.type)) || /\.hei[cf]$/.test(lower(file.name));
}

// iOS قد يسلّم الملف بنوع MIME فارغ، وعندها يرفض Safari فك ترميزه من
// objectURL. نعيد تغليفه بنوع صريح مستنتج من الامتداد.
function toDecodableBlob(file) {
  if (lower(file.type)) return file;

  const name = lower(file.name);
  if (/\.hei[cf]$/.test(name)) return new Blob([file], { type: 'image/heic' });
  if (/\.(jpe?g)$/.test(name)) return new Blob([file], { type: 'image/jpeg' });
  if (/\.png$/.test(name)) return new Blob([file], { type: 'image/png' });
  if (/\.webp$/.test(name)) return new Blob([file], { type: 'image/webp' });

  return file;
}

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(toDecodableBlob(file));
    const image = new Image();

    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('image decode failed'));
    };

    image.src = url;
  });
}

function canvasToBlob(canvas, quality) {
  return new Promise((resolve, reject) => {
    if (typeof canvas.toBlob !== 'function') {
      reject(new Error('toBlob unsupported'));
      return;
    }

    const timer = setTimeout(() => reject(new Error('toBlob timed out')), CONVERT_TIMEOUT_MS);

    canvas.toBlob(
      (blob) => {
        clearTimeout(timer);
        if (blob) resolve(blob);
        else reject(new Error('toBlob returned null'));
      },
      'image/jpeg',
      quality,
    );
  });
}

function replaceExtension(name) {
  const base = String(name || 'photo').replace(/\.[^.]+$/, '');
  return `${base}.jpg`;
}

export default async function compressImage(file) {
  if (!file || typeof document === 'undefined') return file;

  // صور HEIC يجب تحويلها مهما كان حجمها، وإلا سيرفضها الخادم.
  const mustConvert = isHeic(file);
  if (!mustConvert) {
    if (!COMPRESSIBLE_TYPES.has(lower(file.type))) return file;
    if (file.size <= SKIP_BELOW_BYTES) return file;
  }

  try {
    const image = await loadImage(file);
    const { width, height } = image;
    if (!width || !height) return file;

    const scale = Math.min(1, MAX_DIMENSION / Math.max(width, height));
    const targetWidth = Math.max(1, Math.round(width * scale));
    const targetHeight = Math.max(1, Math.round(height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;

    const context = canvas.getContext('2d');
    if (!context) return file;
    // خلفية بيضاء: JPEG لا يدعم الشفافية، وبدونها تصبح المناطق الشفافة سوداء.
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, targetWidth, targetHeight);
    context.drawImage(image, 0, 0, targetWidth, targetHeight);

    const blob = await canvasToBlob(canvas, JPEG_QUALITY);

    // للصور العادية: لو لم يوفّر الضغط شيئًا نرسل الأصل.
    // أما HEIC فنرسل الناتج دائمًا حتى لو كان أكبر، لأن الأصل غير مقروء للخادم.
    if (!mustConvert && blob.size >= file.size) return file;

    return new File([blob], replaceExtension(file.name), {
      type: 'image/jpeg',
      lastModified: Date.now(),
    });
  } catch {
    // أي فشل (ذاكرة، صيغة، متصفح قديم) يعني الرجوع للملف الأصلي؛
    // الخادم عندها يُظهر رسالة واضحة إن كانت الصيغة غير مدعومة.
    return file;
  }
}
